import { useState } from "react"
import { ChevronDown, HelpCircle } from "lucide-react"

export interface FAQItem {
  q: string
  a: string
}

export default function ToolFAQ({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  if (!items || items.length === 0) return null

  // Generate JSON-LD Schema for SEO
  const schemaOrgJSONLD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  }

  return (
    <div className="mt-16">
      {/* 
        We should technically inject this into the document head,
        If react-helmet is not installed, we can just use a standard script tag,
        but React might warn about script tags in body. Let's use dangerouslySetInnerHTML
      */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrgJSONLD) }}
      />
      
      <div className="flex items-center gap-2 mb-6">
        <HelpCircle className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold text-slate-900">Frequently Asked Questions</h2>
      </div>

      <div className="space-y-3">
        {items.map((item, idx) => {
          const isOpen = openIndex === idx
          return (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl bg-white overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full text-left px-6 py-4 flex items-center justify-between focus:outline-none focus:bg-slate-50 transition-colors"
                aria-expanded={isOpen}
              >
                <span className="font-semibold text-slate-900 pr-4">{item.q}</span>
                <ChevronDown
                  className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isOpen && (
                <div className="px-6 pb-4 pt-1 text-slate-600 prose prose-slate max-w-none">
                  {/* Basic text rendering, could support HTML if needed */}
                  <p>{item.a}</p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
